import { useCallback } from 'react'

import { useCrudSelection, useResetEditingState } from '@/atoms'
import { addTrafficArea, updateTrafficArea } from '@/features/domain/traffic'
import { useNotification } from '@/hooks'
import { geo } from '@/server-data'
import { useAppDispatch } from '@/store'

import type { TrafficAreaFormValues } from '../types'

export function useOnSubmit(trafficProfile: uui.domain.client.rm.TrafficProfile) {
  const [, setTrafficAreaSelection] = useCrudSelection('trafficArea')
  const stopEditing = useResetEditingState()
  const dispatch = useAppDispatch()
  const toast = useNotification()

  const recurrence = trafficProfile.id

  const onUpdate = useCallback(
    async (formValues: TrafficAreaFormValues) => {
      try {
        const area = computeTrafficRegionFromFormValues(formValues)
        const thunkResult = await dispatch(
          updateTrafficArea({ area, id: formValues.id, recurrence }),
        )

        if (updateTrafficArea.rejected.match(thunkResult)) {
          throw new Error(thunkResult.payload?.message ?? 'Internal error')
        }

        setTrafficAreaSelection([formValues.id])

        stopEditing()
      } catch (e) {
        toast.error(e.message)
      }
    },
    [dispatch, stopEditing, toast, recurrence],
  )

  const onCreate = useCallback(
    async (formValues: TrafficAreaFormValues) => {
      try {
        const area = computeTrafficRegionFromFormValues(formValues)
        const thunkResult = await dispatch(addTrafficArea({ area, recurrence }))

        if (addTrafficArea.rejected.match(thunkResult)) {
          throw new Error(thunkResult.payload?.message ?? 'Internal error')
        }

        setTrafficAreaSelection([thunkResult.payload])

        stopEditing()
      } catch (e) {
        toast.error(e.message)
      }
    },
    [dispatch, stopEditing, toast, recurrence, setTrafficAreaSelection],
  )

  return useCallback(
    (values: TrafficAreaFormValues) => (values.id ? onUpdate(values) : onCreate(values)),
    [],
  )
}

function computeTrafficRegionFromFormValues(
  formValues: TrafficAreaFormValues,
): uui.domain.server.rm.TrafficRegion {
  const { name, firstTimeWindowValue, secondTimeWindowValue, level, points } = formValues

  const timeWindowLevels: number[] = []
  if (firstTimeWindowValue !== undefined) {
    timeWindowLevels.push(parseValueFromSliderValue(firstTimeWindowValue))
  }
  if (secondTimeWindowValue !== undefined) {
    timeWindowLevels.push(parseValueFromSliderValue(secondTimeWindowValue))
  }

  return {
    level: parseValueFromSliderValue(level),
    name,
    poly: points.map(latLng => geo.latLngToJSON(latLng)),
    timeWindowLevels,
  }
}

function parseValueFromSliderValue(level: number) {
  return Math.abs(level)
}
